import { PATH } from "constants/path"
import { pushTo } from "helpers/history"
import { useAppSelector } from "hooks/useApp"
import {
  getUnreadBuildMiddleware,
  getUnreadConversationMiddleware,
  getUserMiddleware,
} from "pages/auth/services/api"
import { getPendingApprovalsMiddleware } from "./services/api"
import { setUserInfoAction } from "pages/auth/stores/actions"
import { emptyUserInfo, MFAMethod } from "pages/auth/types"
import { createContext, useContext, useEffect } from "react"
import { configureStore } from "stores/configureStore"
import { STATUS_RESPONSE } from "types"
import { PageLayoutProjectProps } from "../projects/types"
import { twMerge } from "tailwind-merge"
import MenuLeft from "./MenuLeft"
import ModalCustom from "components/ModalCustom"
import Button from "components/Button/Button"
import CheckedDefault from "components/Checked/CheckedDefault"
import { useBoolean } from "helpers/hooks"
import {
  createAgreementWithLastestDocument,
  getReleaseNotesMiddleware,
} from "./services/api"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { Link, useLocation } from "react-router-dom"
import {
  isValidPhone,
  umamiIdentifySession,
  umamiTracking,
} from "helpers/utils"
import { EVENT } from "constants/events"
import { WarningEnhanceAuthenticationModal } from "./modal/WarningEnhanceAuthenticationModal"
import { includes } from "lodash"
import { WarningSecurityCodeAndPhoneNumberModal } from "./modal/WarningSecurityCodeAndPhoneNumberModal"
import { WarningNewOrganizationModal } from "./modal/WarningNewOrganizationModal"
import { VaultKeyRecoveryModal } from "./modal/VaultKeyRecoveryModal"
import { PrivateKeyRecoveryModal } from "./modal/PrivateKeyRecoveryModal"
import { KeyHealthDetailModal } from "./modal/KeyHealthDetailModal"
import { removeWsCodePrefix } from "helpers/wsCode.helper"
import { PendingApprovalCard } from "./PendingApprovalCard"
import { KeyHealthStatusBanner } from "components/KeyHealthStatusBanner/KeyHealthStatusBanner"
import { useKeyHealthCheck } from "hooks/useKeyHealthCheck"
import HealthCheckLoadingOverlay from "./HealthCheckLoadingOverlay"
import {
  clearLocalStorage,
  getNewOrganization,
  getRemindMeLater,
  getUserEmail,
  setNewOrganization,
} from "helpers/local_storage"

interface PageLayoutHealthContextValue {
  isPreCheckReady: boolean
  keyAvailability: {
    project: Record<string, boolean>
    build: Record<string, boolean>
    conversation: Record<string, boolean>
  }
}

export const PageLayoutHealthContext =
  createContext<PageLayoutHealthContextValue>({
    isPreCheckReady: true,
    keyAvailability: { project: {}, build: {}, conversation: {} },
  })

export const usePageLayoutHealth = () => useContext(PageLayoutHealthContext)

const PageLayout = (props: PageLayoutProjectProps) => {
  const {
    children,
    childrenHeader,
    heightHeader = 64,
    minWidth = "min-w-[1100px]",
    containerClass,
    containerStyles,
    contentClass,
    contentStyles,
    headerClass,
    headerStyles,
    childrenWarning,
  } = props

  const userInfo = useAppSelector((state) => state.userInfo)
  const unreadBuild = useAppSelector((state) => state.unreadBuild)
  const unreadConversation = useAppSelector((state) => state.unreadConversation)
  const pendingApprovals = useAppSelector((state) => state.pendingApprovals)
  const checkbox = useBoolean(false)
  const seenNote = useBoolean(true)
  const isWarningEnhanceAuthentication = useBoolean(false)
  const isWarningSecurityCodeAndPhoneNumber = useBoolean(false)
  const isWarningNewOrganization = useBoolean(false)
  const isViewingApprovalDetail = useBoolean(false)

  const {
    bannerStatus,
    bannerScope,
    fixProgress,
    isAutoFixing,
    isDetailModalOpen,
    openDetailModal,
    closeDetailModal,
    needsVaultRecovery,
    onVaultRecoverySuccess,
    needsPrivateKeyRecovery,
    onPrivateKeyRecoverySuccess,
    isPreCheckReady,
    keyAvailability,
  } = useKeyHealthCheck({
    userId: userInfo.id,
    isEncrypted: userInfo.is_encrypted ?? false,
  })

  const location = useLocation()

  useEffect(() => {
    if (userInfo.id) {
      return
    }
    getUserMiddleware((type) => {
      if (type === STATUS_RESPONSE.ERROR) {
        clearLocalStorage()
        configureStore.dispatch(setUserInfoAction(emptyUserInfo))
        pushTo(PATH.login)
        return
      }
      getUnreadBuildMiddleware()
      getUnreadConversationMiddleware()
      getReleaseNotes()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!userInfo.id) {
      return
    }
    getPendingApprovalsMiddleware()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.id])

  useEffect(() => {
    if (!userInfo.id) return
    const email = (userInfo.email || getUserEmail()).trim()
    if (!email) return
    umamiIdentifySession({
      email,
      name: userInfo.fullname || undefined,
      internalUserId: userInfo.id,
    })
  }, [userInfo.id, userInfo.email, userInfo.fullname])

  useEffect(() => {
    // Auto show detail if only 1 approval
    if (
      pendingApprovals?.total === 1 &&
      pendingApprovals?.data &&
      pendingApprovals.data.length > 0 &&
      !isViewingApprovalDetail.value
    ) {
      isViewingApprovalDetail.setValue(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApprovals?.total, pendingApprovals?.data])

  useEffect(() => {
    if (!userInfo?.id) return
    const googleAuthenticator = userInfo.mfaMethods?.filter(
      (method) => Number(method) !== MFAMethod.email
    )
    const pathnameWithoutWsCode = removeWsCodePrefix(location.pathname)

    if (
      googleAuthenticator?.length ||
      includes([PATH.general], pathnameWithoutWsCode)
    ) {
      isWarningEnhanceAuthentication.setValue(false)
      return
    }
    isWarningEnhanceAuthentication.setValue(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id, location.pathname])

  useEffect(() => {
    if (!userInfo?.id) {
      isWarningSecurityCodeAndPhoneNumber.setValue(false)
      return
    }
    const pathnameWithoutWsCode = removeWsCodePrefix(location.pathname)
    if (
      isValidPhone(userInfo?.phone_number) ||
      includes(
        [PATH.general, PATH.updatePhoneNumberAndSecurityCode],
        pathnameWithoutWsCode
      )
    ) {
      isWarningSecurityCodeAndPhoneNumber.setValue(false)
      return
    }
    const remindMeLater = getRemindMeLater()
    if (remindMeLater === "true") {
      isWarningSecurityCodeAndPhoneNumber.setValue(false)
      return
    }
    isWarningSecurityCodeAndPhoneNumber.setValue(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id, userInfo?.phone_number, location.pathname])

  useEffect(() => {
    if (!userInfo?.id) {
      isWarningNewOrganization.setValue(false)
      return
    }
    const newWarningOrganization = getNewOrganization()
    if (newWarningOrganization === "true") {
      isWarningNewOrganization.setValue(false)
      return
    }
    const organizationOwners = (userInfo.organizations || []).filter(
      (el) => el.type === "OWNER"
    )
    if (userInfo.is_added_organization && organizationOwners?.length === 0) {
      isWarningNewOrganization.setValue(true)
      return
    }
    isWarningNewOrganization.setValue(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id])

  const getReleaseNotes = () => {
    getReleaseNotesMiddleware()
      .then(() => {
        seenNote.setValue(false)
      })
      .catch(() => {
        seenNote.setValue(true)
      })
  }

  const onConfirm = () => {
    if (!checkbox.value) return
    createAgreementWithLastestDocument()
      .then(() => {
        configureStore.dispatch(
          setUserInfoAction({ ...userInfo, has_agreed_latest_document: true })
        )
      })
      .catch(() => {
        return (
          <LabelNotificationPage
            messenger="Something went wrong. Please try again!"
            type="error"
          />
        )
      })
  }

  const handleEnhanceAuthentication = () => {
    pushTo(PATH.general, undefined, { tab: 2 })
  }

  const renderModal = () => {
    if (needsVaultRecovery) {
      return <VaultKeyRecoveryModal onSuccess={onVaultRecoverySuccess} />
    }
    if (needsPrivateKeyRecovery) {
      return <PrivateKeyRecoveryModal onSuccess={onPrivateKeyRecoverySuccess} />
    }
    if (isDetailModalOpen) {
      return (
        <KeyHealthDetailModal
          fixProgress={fixProgress}
          isAutoFixing={isAutoFixing}
          scope={bannerScope}
          onClose={closeDetailModal}
        />
      )
    }
    if (!userInfo.has_agreed_latest_document) {
      return (
        <ModalCustom
          label="Confirmation"
          widthModal={570}
          bodyChildren={
            <div
              className="grid text-sm mb-[12px] font-medium gap-y-2 text-center sm:text-center md:text-justify "
              style={{ color: "black" }}
            >
              <p className=" pb-[5px]">
                {" "}
                We have updated our Terms and Conditions, Privacy Policy
              </p>
              <div className="flex">
                {" "}
                <CheckedDefault
                  checked={checkbox.value}
                  onClick={() => checkbox.setValue(!checkbox.value)}
                />{" "}
                <p className="pl-[18px] sm:pl-[26px]">
                  {" "}
                  By clicking on this button, I agree with Tracelium's{" "}
                  <Link
                    to={{ pathname: PATH.termsAndServices }}
                    target="_blank"
                    style={{ color: "blue", textDecoration: "underline" }}
                    onClick={() => umamiTracking(EVENT.TERM)}
                  >
                    {" "}
                    Terms and Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    to={{ pathname: PATH.policies }}
                    target="_blank"
                    style={{ color: "blue", textDecoration: "underline" }}
                    onClick={() => umamiTracking(EVENT.POLICY)}
                  >
                    {" "}
                    Privacy Policy
                  </Link>
                  {"."}
                </p>
              </div>
              {!checkbox.value ? (
                <div style={{ color: "red" }} className="text-center pb-[30px]">
                  Please Check and accept our Terms and Conditions, Privacy
                  Policy
                </div>
              ) : (
                <div className="pb-[30px] h-[70px] sm:h-[50px]"></div>
              )}
              <div className="flex items-center justify-center ">
                <Button
                  widthBtn={"178px"}
                  title="Accept"
                  colorBtn="yellow"
                  onClick={onConfirm}
                  disabled={!checkbox.value}
                />
              </div>
            </div>
          }
        />
      )
    }
    if (isWarningEnhanceAuthentication.value) {
      return (
        <WarningEnhanceAuthenticationModal
          onSetUpGoogleAuthenticator={handleEnhanceAuthentication}
        />
      )
    }
    if (isWarningSecurityCodeAndPhoneNumber.value) {
      return (
        <WarningSecurityCodeAndPhoneNumberModal
          handleUpdateSecurityCodeAndPhoneNumber={(currentPassword) => {
            isWarningSecurityCodeAndPhoneNumber.setValue(false)
            pushTo(PATH.updatePhoneNumberAndSecurityCode, undefined, {
              isSuccessPassword: true,
              currentPassword,
            })
          }}
          onCloseModal={() =>
            isWarningSecurityCodeAndPhoneNumber.setValue(false)
          }
        />
      )
    }
    if (isWarningNewOrganization.value) {
      return (
        <WarningNewOrganizationModal
          onTryItNowOrganization={() => {
            isWarningNewOrganization.setValue(false)
            setNewOrganization("true")
            pushTo(PATH.organizationAdd)
          }}
          onCloseModal={() => isWarningNewOrganization.setValue(false)}
        />
      )
    }
    return null
  }

  return (
    <div
      className={twMerge(
        "height-screen min-w-full flex flex-col md:flex-row relative overflow-hidden",
        containerClass
      )}
      style={containerStyles}
    >
      <MenuLeft
        userInfo={userInfo}
        unreadBuild={unreadBuild}
        unreadConversation={unreadConversation}
      />
      <PageLayoutHealthContext.Provider
        value={{ isPreCheckReady, keyAvailability }}
      >
        <div
          className={`flex flex-col flex-1 b-grayWhite2 overflow-auto md:${minWidth} relative`}
        >
          {pendingApprovals?.total && pendingApprovals.total > 0 ? (
            <PendingApprovalCard pendingApprovals={pendingApprovals} />
          ) : null}

          <KeyHealthStatusBanner
            status={bannerStatus}
            scope={bannerScope}
            isAutoFixing={isAutoFixing}
            onClickYellow={openDetailModal}
            onClickGreen={fixProgress.length > 0 ? openDetailModal : undefined}
          />

          {/* header */}
          {childrenHeader ? (
            <div
              className={twMerge("border-b border-[#E4E4E4]", headerClass)}
              style={{
                background: "#FFFFFF",
                minHeight: heightHeader,
                ...headerStyles,
              }}
            >
              {childrenHeader}
            </div>
          ) : null}
          {childrenWarning ? childrenWarning : null}

          {/* main content */}
          <div
            className={twMerge(
              "overflow-auto h-full bg-grayWhite2 relative",
              contentClass
            )}
            style={contentStyles}
          >
            {!isPreCheckReady &&
            !location.pathname.includes("conversations") &&
            !location.pathname.includes("/build/") ? (
              <HealthCheckLoadingOverlay scope={bannerScope} />
            ) : (
              children
            )}
          </div>
        </div>
      </PageLayoutHealthContext.Provider>
      {renderModal()}
    </div>
  )
}

export default PageLayout
